import * as XLSX from 'xlsx';

export function exportToExcel(sheetsData, fileName) {
  const workbook = XLSX.utils.book_new();

  Object.entries(sheetsData).forEach(([sheetName, data]) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase().replace(/[-_]+/g, " ");

const getScreenerWindowId = (value) => {
  const text = normaliseComparableText(value);

  if (!text) return "";
  if (text.includes("baseline") || text.includes("autumn") || text.includes("beginning of year") || text === "boy") return "baseline";
  if (text.includes("midpoint") || text.includes("mid point") || text.includes("mid year") || text.includes("midyear") || text === "moy" || text.includes("spring")) return "midpoint";
  if (text.includes("end of year") || text.includes("endofyear") || text.includes("end year") || text.includes("endyear") || text.includes("eoy") || text.includes("summer")) return "end-of-year";

  return "";
};

const getScreenerLabel = (windowId) => {
  if (windowId === "baseline") return "Baseline";
  if (windowId === "midpoint") return "Midpoint";
  if (windowId === "end-of-year") return "End of Year";
  return "";
};

const getFirstPresent = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const getAssessmentId = (value, includeGenericId = false) =>
  getFirstPresent(
    value?.assignedAssessmentId,
    value?.assigned_assessment_id,
    value?.assessmentId,
    value?.assessmentid,
    value?.assessment_id,
    value?.screenerId,
    value?.screenerid,
    value?.screener_id,
    value?.assignedScreenerId,
    value?.assigned_screener_id,
    includeGenericId ? value?.id : ""
  );

const getScreenerName = (value) =>
  getFirstPresent(
    value?.assignedScreenerName,
    value?.assigned_screener_name,
    value?.screenerName,
    value?.screener_name,
    value?.assessmentName,
    value?.assessment_name,
    value?.screeningWindow,
    value?.screening_window,
    value?.screeningWindowId,
    value?.screening_window_id,
    value?.screenerWindow,
    value?.screener_window,
    value?.screenerWindowId,
    value?.screener_window_id,
    value?.windowName,
    value?.window_name,
    value?.windowId,
    value?.window_id,
    value?.name,
    value?.title,
    value?.label
  );

const getLinkFromValue = (value) =>
  getFirstPresent(
    value?.assessmentQrLink,
    value?.assessmentQRLink,
    value?.assessment_qr_link,
    value?.assessmentLink,
    value?.assessment_link,
    value?.bypassLink,
    value?.bypass_link,
    value?.qrLink,
    value?.qr_link,
    value?.link,
    value?.url
  );

const getAssessmentSpecificRowLink = (value) =>
  getFirstPresent(
    value?.assessmentQrLink,
    value?.assessmentQRLink,
    value?.assessment_qr_link,
    value?.assessmentLink,
    value?.assessment_link,
    value?.qrLink,
    value?.qr_link
  );

const isCompletedAssessment = (assessment) => {
  const status = normaliseComparableText(assessment?.status || assessment?.assignmentStatus || assessment?.completionStatus);
  return assessment?.completed === true || assessment?.isCompleted === true || status === "completed";
};

const isAssignedAssessment = (assessment) => {
  const status = normaliseComparableText(assessment?.status || assessment?.assignmentStatus || assessment?.assignment_status);

  return (
    assessment?.assigned === true ||
    assessment?.isAssigned === true ||
    ["assigned", "active", "pending", "not started", "in progress", "ready", "open"].includes(status) ||
    (getAssessmentId(assessment, true) && assessment?.completed === false)
  );
};

const getAssessmentCandidates = (row) => [
  row?.assignedScreener,
  row?.assigned_screener,
  row?.assignedAssessment,
  row?.assigned_assessment,
  ...(Array.isArray(row?.assignedScreeners) ? row.assignedScreeners : []),
  ...(Array.isArray(row?.assigned_screeners) ? row.assigned_screeners : []),
  ...(Array.isArray(row?.assignedAssessments) ? row.assignedAssessments : []),
  ...(Array.isArray(row?.assigned_assessments) ? row.assigned_assessments : []),
  ...(Array.isArray(row?.assessments) ? row.assessments : []),
  ...(Array.isArray(row?.screeners) ? row.screeners : [])
].filter(Boolean);

export function getAssignedScreener(row = {}) {
  const rowAssessment = getAssessmentId(row);
  const rowWindowId = getScreenerWindowId(getScreenerName(row) || rowAssessment);

  if (rowAssessment || rowWindowId) {
    return {
      assessmentId: rowAssessment,
      windowId: rowWindowId,
      name: getScreenerLabel(rowWindowId) || getScreenerName(row),
      link: getAssessmentSpecificRowLink(row)
    };
  }

  const candidates = getAssessmentCandidates(row);
  const assignedCandidate =
    candidates.find((candidate) => isAssignedAssessment(candidate) && !isCompletedAssessment(candidate)) ||
    candidates.find((candidate) => !isCompletedAssessment(candidate)) ||
    (candidates.length === 1 ? candidates[0] : null);

  if (!assignedCandidate) {
    return null;
  }

  const assessmentId = getAssessmentId(assignedCandidate, true);
  const windowId = getScreenerWindowId(getScreenerName(assignedCandidate) || assessmentId);

  return {
    assessmentId,
    windowId,
    name: getScreenerLabel(windowId) || getScreenerName(assignedCandidate),
    link: getLinkFromValue(assignedCandidate)
  };
}

const buildInitialQrCodeLink = (row) => {
  const studentId = row?.originalId || row?.studentId || row?.studentid || row?.id;
  const password = row?.password;

  if (!studentId || !password) return "";

  const assessmentURL = process.env.REACT_APP_QR_CODE_BASE_URL + "/?code=";
  let removeHyphens = String(studentId).replaceAll("-", "");
  let first32 = removeHyphens.substring(0, 32);
  let newQrCode = first32.concat(password);
  return assessmentURL.concat(newQrCode);
};

export function buildAssessmentQrLink(row) {
  return buildInitialQrCodeLink(row);
}

export function exportLoginBypassRowsToExcel(rows, fileName, className = "") {
  const workbook = XLSX.utils.book_new();
  const shouldShowScreener = (Array.isArray(rows) ? rows : []).some((row) => getAssignedScreener(row)?.name);
  const excelRows = (Array.isArray(rows) ? rows : []).map((row) => ({
    Class: row.className || row.class || className,
    "Student Name": row.name || row.studentName || row.fullName || "",
    ...(shouldShowScreener ? { Screener: getAssignedScreener(row)?.name || "" } : {}),
    "Bypass Link": buildAssessmentQrLink(row),
    "Class Type": row.classType || row.class_type || (row.isSnapshot || row.snapshot ? "Snapshot" : "Active")
  }));
  const worksheet = XLSX.utils.json_to_sheet(excelRows);

  worksheet["!cols"] = [
    { wch: 24 },
    { wch: 28 },
    ...(shouldShowScreener ? [{ wch: 18 }] : []),
    { wch: 80 },
    { wch: 12 }
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, "Student Bypass Links");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}


// export function exportToExcel(data, fileName) {
//   const workbook = XLSX.utils.book_new();
//   const worksheet = XLSX.utils.json_to_sheet(data);
//   XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
//   XLSX.writeFile(workbook, `${fileName}.xlsx`);
// }


// function getScoreColor(score) {
//     if (score < 0.2) return { fill: { fgColor: { rgb: "FFFF0000" } } }; // Red for scores < 0.2
//     if (score >= 0.2 && score < 0.4) return { fill: { fgColor: { rgb: "FFFFFF00" } } }; // Yellow for scores >= 0.2 and < 0.4
//     return {};
// }

// function createStyledWorksheet(data) {
//     const worksheet = XLSX.utils.json_to_sheet(data, {skipHeader: true});
//     const ref = XLSX.utils.decode_range(worksheet['!ref']);

//     for (let R = ref.s.r; R <= ref.e.r; ++R) {
//         for (let C = ref.s.c; C <= ref.e.c; ++C) {
//             const cell_address = { c: C, r: R };
//             const cell_ref = XLSX.utils.encode_cell(cell_address);

//             const cell = worksheet[cell_ref];
//             if (cell && R > 0) { // Skip header row
//                 const score = parseFloat(cell.v);
//                 if (!isNaN(score)) {
//                     cell.s = getScoreColor(score);
//                 }
//             }
//         }
//     }
//     return worksheet;
// }

// export function exportToExcel(sheetsData, fileName) {
//     const workbook = XLSX.utils.book_new();

//     Object.entries(sheetsData).forEach(([sheetName, data]) => {
//         const worksheet = createStyledWorksheet(data);
//         XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
//     });

//     XLSX.writeFile(workbook, `${fileName}.xlsx`);
// }
